import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button, Link } from "@/libraries/design/components";
import { wacToken, ColumnLayout } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { wacRefreshQ4Fy26Experiment } from "@/utils/experiment/wacRefreshExperiment";

import {
    CustomerQuote,
    CustomerQuoteCarousel
} from "./customer-quote-carousel";
import { ConnectorLogo, LogosMarquee } from "./logos-marquee";

export type { ConnectorLogo } from "./logos-marquee";
export type { CustomerQuote } from "./customer-quote-carousel";

export interface ConnectorProps {
    title: string;
    text: string;
    linkText?: string;
    linkHref?: string;
    linkDataEventProps?: DataEventProps;
    customerQuotes: CustomerQuote[];
    mobileLogos: ConnectorLogo[];
    tabletLogos: ConnectorLogo[];
    desktopLogos: ConnectorLogo[];
}

export const Connector = ({
    title,
    text,
    linkText,
    linkHref,
    linkDataEventProps,
    customerQuotes,
    mobileLogos,
    tabletLogos,
    desktopLogos
}: ConnectorProps) => {
    const checkWacRefresh = useExperiment(wacRefreshQ4Fy26Experiment);
    const isWacRefresh =
        checkWacRefresh.type === EnrollmentType.ENROLLED &&
        checkWacRefresh.value;

    return (
        <div css={[containerCss, isWacRefresh && refreshContainerCss]}>
            <div css={isWacRefresh && refreshColumnOverrideCss}>
                <ColumnLayout layout="5-7">
                    <div
                        className="contentBlock"
                        css={[
                            lightTextCss,
                            isWacRefresh && refreshTextBlockCss
                        ]}
                    >
                        <h2 css={[titleCss, isWacRefresh && refreshTitleCss]}>
                            {title}
                        </h2>
                        <p css={[textCss, isWacRefresh && refreshBodyTextCss]}>
                            {text}
                        </p>
                        {linkText && linkHref && (
                            <div css={linkCss}>
                                {isWacRefresh ? (
                                    <Button
                                        href={linkHref}
                                        variant="secondary"
                                        colorAgainst="white"
                                        responsiveSize={{
                                            mobile: "small",
                                            tablet: "small",
                                            desktop: "large",
                                            xl_desktop: "large"
                                        }}
                                        dataEventProps={linkDataEventProps}
                                    >
                                        {linkText}
                                    </Button>
                                ) : (
                                    <Link
                                        aria-label={linkText}
                                        text={linkText}
                                        href={linkHref}
                                        type="primary"
                                        bgColor="bgBlackBlue"
                                        {...(linkDataEventProps && {
                                            dataEventProps: linkDataEventProps
                                        })}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <CustomerQuoteCarousel customerQuotes={customerQuotes} />
                </ColumnLayout>
            </div>
            <LogosMarquee
                mobileLogos={mobileLogos}
                tabletLogos={tabletLogos}
                desktopLogos={desktopLogos}
            />
        </div>
    );
};

export default Connector;

const containerCss = css({
    paddingBlock: token("space.1000"),
    backgroundColor: wacToken("color.primary.black"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.600")
});

const refreshContainerCss = css({
    backgroundColor: wacToken("color.neutral.light100"),
    paddingBlock: "96px",
    gap: "96px",

    [media.below.md]: {
        paddingBlock: "80px",
        gap: "80px"
    },

    [media.below.sm]: {
        paddingBlock: "60px",
        gap: "60px"
    }
});

const lightTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p": {
        color: wacToken("color.neutral.light0")
    },

    [media.below.sm]: {
        textAlign: "center"
    }
});

const refreshTextBlockCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p": {
        color: wacToken("color.primary.black")
    },

    [media.below.sm]: {
        textAlign: "left"
    }
});

const titleCss = css({
    marginBlockStart: token("space.400"),
    marginBlockEnd: token("space.100"),

    [media.below.sm]: {
        marginBlockStart: 0
    }
});

const refreshTitleCss = css({
    marginBlockStart: 0,
    marginBlockEnd: 0
});

const textCss = css({
    marginBlock: 0
});

const refreshBodyTextCss = css({
    marginBlockStart: token("space.150")
});

const refreshColumnOverrideCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        [media.below.md]: {
            gridTemplateColumns: "1fr"
        }
    }
});

const linkCss = css({
    marginBlockStart: token("space.300"),
    marginBlockEnd: token("space.400"),

    [media.below.sm]: {
        marginBlockEnd: token("space.300")
    }
});
